import { useState, useEffect } from 'react';
import { useInventory } from '../contexts/InventoryContext';
import { supabase } from '../lib/supabase';
import { getAdminId } from '../utils/getAdminId';
import { User } from '../types';

interface StockAnalytics {
  totalValue: number;
  categoryBreakdown: Record<string, { count: number; value: number; percentage: number }>;
  monthlyTrends: Array<{ date: string; incoming: number; outgoing: number; total: number }>;
  topCategories: Array<{ category: string; count: number; value: number }>;
  lowStockItems: Array<{ id: string; name: string; quantity: number; category: string }>;
}

export const useStockAnalytics = (user?: User | null) => {
  const { items, shipments } = useInventory();
  const [analytics, setAnalytics] = useState<StockAnalytics>({
    totalValue: 0,
    categoryBreakdown: {},
    monthlyTrends: [],
    topCategories: [],
    lowStockItems: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    calculateAnalytics();
  }, [items, shipments, user]);

  const calculateMonthlyTrends = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);

      const adminId = getAdminId(user);

      // Fetch incoming shipments with items
      let incomingQuery = supabase
        .from('incoming_shipments')
        .select('id, created_at, received_date, shipment_items(received_quantity)')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (adminId) {
        incomingQuery = incomingQuery.eq('admin_id', adminId);
      }

      const { data: incomingShipments, error: incomingError } = await incomingQuery;
      if (incomingError) throw incomingError;

      // Fetch outgoing shipments with items
      let outgoingQuery = supabase
        .from('outgoing_shipments')
        .select('id, created_at, shipping_date, outgoing_shipment_items(quantity)')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (adminId) {
        outgoingQuery = outgoingQuery.eq('admin_id', adminId);
      }

      const { data: outgoingShipments, error: outgoingError } = await outgoingQuery;
      if (outgoingError) throw outgoingError;

      // Create daily buckets for the last 30 days
      const dailyData: Record<string, { incoming: number; outgoing: number; date: Date }> = {};

      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateKey = date.toISOString().split('T')[0];
        dailyData[dateKey] = {
          incoming: 0,
          outgoing: 0,
          date: date
        };
      }

      // Aggregate incoming shipments by day
      incomingShipments?.forEach(shipment => {
        const dateKey = new Date(shipment.created_at).toISOString().split('T')[0];
        if (dailyData[dateKey]) {
          const items = shipment.shipment_items || [];
          const totalQuantity = items.reduce((sum: number, item: any) =>
            sum + (item.received_quantity || 0), 0);
          dailyData[dateKey].incoming += totalQuantity;
        }
      });

      // Aggregate outgoing shipments by day
      outgoingShipments?.forEach(shipment => {
        const dateKey = new Date(shipment.created_at).toISOString().split('T')[0];
        if (dailyData[dateKey]) {
          const items = shipment.outgoing_shipment_items || [];
          const totalQuantity = items.reduce((sum: number, item: any) =>
            sum + (item.quantity || 0), 0);
          dailyData[dateKey].outgoing += totalQuantity;
        }
      });

      // Calculate running total of inventory
      let runningTotal = items.length;
      const trends = Object.values(dailyData)
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map(day => {
          const netChange = day.incoming - day.outgoing;
          runningTotal = Math.max(0, runningTotal + netChange);

          return {
            date: day.date.toLocaleDateString(),
            incoming: day.incoming,
            outgoing: day.outgoing,
            total: runningTotal
          };
        });

      return trends;
    } catch (error) {
      console.error('Failed to calculate monthly trends:', error);

      // Fallback to basic data if fetch fails
      return Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (29 - i));
        return {
          date: date.toLocaleDateString(),
          incoming: 0,
          outgoing: 0,
          total: items.length
        };
      });
    }
  };

  const calculateAnalytics = async () => {
    setLoading(true);

    try {
      // Calculate total value
      const totalValue = items.reduce((sum, item) => 
        sum + ((item.buy_price || 0) * item.quantity), 0
      );

      // Category breakdown
      const categoryBreakdown = items.reduce((acc, item) => {
        if (!acc[item.category]) {
          acc[item.category] = { count: 0, value: 0, percentage: 0 };
        }
        acc[item.category].count++;
        acc[item.category].value += (item.buy_price || 0) * item.quantity;
        return acc;
      }, {} as Record<string, { count: number; value: number; percentage: number }>);

      // Calculate percentages
      Object.keys(categoryBreakdown).forEach(category => {
        categoryBreakdown[category].percentage = 
          totalValue > 0 ? (categoryBreakdown[category].value / totalValue) * 100 : 0;
      });

      // Calculate real monthly trends from shipments data
      const monthlyTrends = await calculateMonthlyTrends();

      // Top categories by count
      const topCategories = Object.entries(categoryBreakdown)
        .sort(([,a], [,b]) => b.count - a.count)
        .slice(0, 5)
        .map(([category, data]) => ({ category, ...data }));

      // Low stock items
      const lowStockItems = items
        .filter(item => item.quantity <= 10)
        .map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          category: item.category
        }))
        .sort((a, b) => a.quantity - b.quantity);

      setAnalytics({
        totalValue,
        categoryBreakdown,
        monthlyTrends,
        topCategories,
        lowStockItems
      });
    } catch (error) {
      console.error('Failed to calculate analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    analytics,
    loading,
    refreshAnalytics: calculateAnalytics
  };
};